import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Plus, UserPlus, Pencil, Link as LinkIcon, Eye, Trash2, Wand2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService, { type OnboardingSession } from '@/services/tenantConfig.service';
import ApiService from '@/services/api.service';
import { useNavigate } from 'react-router-dom';

export default function OnboardingTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [promptText, setPromptText] = useState('');
  const [onboarderName, setOnboarderName] = useState('');
  const [onboarderEmail, setOnboarderEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Initial message state
  const [initialMessage, setInitialMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);


  // Edit state
  const [editSession, setEditSession] = useState<OnboardingSession | null>(null);
  const [editContext, setEditContext] = useState('');
  const [editInitialMessage, setEditInitialMessage] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const handleGenerateGreeting = useCallback(async () => {
    setIsGenerating(true);
    try {
      const greeting = await TenantConfigService.getInstance().generateOnboardingGreeting(promptText.trim(), onboarderName.trim());
      setInitialMessage(greeting);
    } catch (err: any) {
      showToast.error('Failed to generate greeting', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [promptText]);

  // Delete state
  const [deleteSession, setDeleteSession] = useState<OnboardingSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['onboarding-sessions'],
    queryFn: () => TenantConfigService.getInstance().listOnboardingSessions(),
  });

  const resetForm = useCallback(() => {
    setPromptText('');
    setOnboarderName('');
    setOnboarderEmail('');
    setInitialMessage('');
  }, []);

  const handleCreate = useCallback(async () => {
    if (!onboarderEmail.trim() || !onboarderEmail.includes('@')) return;

    setIsCreating(true);
    try {
      await TenantConfigService.getInstance().createOnboardingSession({
        prompt_text: promptText.trim(),
        onboarder_name: onboarderName.trim(),
        onboarder_email: onboarderEmail.trim(),
        ...(initialMessage.trim() ? {
          initial_message: { role: 'assistant', content: initialMessage.trim() },
        } : {}),
      });
      showToast.success('Onboarding session created successfully');
      setDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    } catch (err: any) {
      showToast.error('Failed to create onboarding session', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsCreating(false);
    }
  }, [promptText, onboarderName, onboarderEmail, initialMessage, resetForm, queryClient]);

  const handleCreateLink = useCallback(async (threadId: string) => {
    try {
      const result = await ApiService.getInstance().request(
        'POST',
        `/tenant-config/onboarding-sessions/${threadId}/create-link`,
        {}
      ) as { url: string };
      await navigator.clipboard.writeText(result.url);
      showToast.success('Link copied to clipboard');
    } catch (err: any) {
      showToast.error('Failed to create link', {
        description: err.message || 'An error occurred',
      });
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteSession?.thread_id) return;
    setIsDeleting(true);
    try {
      await ApiService.getInstance().request('DELETE', `/tenant-config/onboarding-sessions/${deleteSession.thread_id}`);
      showToast.success('Session deleted');
      setDeleteSession(null);
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    } catch (err: any) {
      showToast.error('Failed to delete session', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteSession, queryClient]);

  const handleOpenEdit = useCallback((session: OnboardingSession) => {
    setEditSession(session);
    setEditContext(session.additional_context);
    setEditInitialMessage(session.initial_message?.content || '');
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editSession?.thread_id) return;
    setIsSavingEdit(true);
    try {
      await ApiService.getInstance().request('PATCH', `/tenant-config/onboarding-sessions/${editSession.thread_id}`, {
        additional_context: editContext,
        initial_message: editInitialMessage.trim()
          ? { role: 'assistant', content: editInitialMessage.trim() }
          : null,
      });
      showToast.success('Context updated');
      setEditSession(null);
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions'] });
    } catch (err: any) {
      showToast.error('Failed to update context', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsSavingEdit(false);
    }
  }, [editSession, editContext, editInitialMessage, queryClient]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="max-w-4xl space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Onboarding Sessions</CardTitle>
              <CardDescription>
                AI-guided sessions to help new users connect their data sources.
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Onboarding Session
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <UserPlus className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No onboarding sessions yet. Create one to invite a user and guide them through
                connecting their data.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Shared With</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.thread_id}>
                    <TableCell className="font-medium">{session.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {session.shared_with.join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(session.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => session.thread_id && handleCreateLink(session.thread_id)}>
                          <LinkIcon className="h-3 w-3 mr-1" />
                          Copy Link
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => session.thread_id && navigate(`/onboarding/conversation/${session.thread_id}`)} title="View thread">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(session)} title="Edit prompt">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteSession(session)} title="Delete session">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!isCreating) {
            setDialogOpen(open);
            if (!open) resetForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl p-8">
          {isCreating ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Creating onboarding session...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Setting up the agent, thread, and sending invite.
                </p>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Add Onboarding Session</DialogTitle>
                <DialogDescription>
                  Create an onboarding session for a new user.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4 px-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="onboarder-name">Name</Label>
                    <Input
                      id="onboarder-name"
                      value={onboarderName}
                      onChange={(e) => setOnboarderName(e.target.value)}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="onboarder-email">Email</Label>
                    <Input
                      id="onboarder-email"
                      type="email"
                      value={onboarderEmail}
                      onChange={(e) => setOnboarderEmail(e.target.value)}
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="prompt-text">
                    Context for AI Assistant (optional)
                  </Label>
                  <Textarea
                    id="prompt-text"
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    placeholder="Provide any additional context about this user's business, data sources they might have, or specific goals for the onboarding..."
                    rows={5}
                  />
                  <p className="text-sm text-muted-foreground">
                    This context will be included in the AI assistant's system
                    prompt to help guide the onboarding conversation.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="initial-message">
                      Initial Agent Message (optional)
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-1"
                      onClick={handleGenerateGreeting}
                      disabled={isGenerating || !promptText.trim()}
                      title="Generate greeting from context"
                    >
                      {isGenerating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Wand2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Textarea
                    id="initial-message"
                    value={initialMessage}
                    onChange={(e) => setInitialMessage(e.target.value)}
                    placeholder="Hey! I'm here to help get your Shopify and QuickBooks data connected..."
                    rows={3}
                  />
                  <p className="text-sm text-muted-foreground">
                    This message will appear as the first message in the conversation.
                    Click the wand to auto-generate from the context above.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!onboarderEmail.trim() || !onboarderEmail.includes('@')}
                >
                  Create Session
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editSession} onOpenChange={(open) => { if (!open) setEditSession(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Onboarding Session</DialogTitle>
            <DialogDescription>
              Shared with: {editSession?.shared_with?.join(', ') || '—'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-context">Context for AI Assistant</Label>
                <Textarea
                  id="edit-context"
                  value={editContext}
                  onChange={(e) => setEditContext(e.target.value)}
                  placeholder="Provide context about the user's business, data sources, or goals..."
                  rows={5}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-initial-message">Initial Agent Message</Label>
                <Textarea
                  id="edit-initial-message"
                  value={editInitialMessage}
                  onChange={(e) => setEditInitialMessage(e.target.value)}
                  placeholder="Hey! I'm here to help get your data connected..."
                  rows={3}
                />
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSession(null)} disabled={isSavingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
              {isSavingEdit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteSession} onOpenChange={(open) => { if (!open) setDeleteSession(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Onboarding Session</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this session{deleteSession?.shared_with?.length ? ` shared with ${deleteSession.shared_with.join(', ')}` : ''}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSession(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
